import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}
// Convert from time HH:MM in EST to cron format MM HH * * *
export function convertTimeToCron(time: string, daysSent?: string[]): string {
	// Split the time string into hours and minutes
	const [hours, minutes] = time.split(':').map(Number);

	// Create a mapping of days to cron format (0-6 where 0 = Sunday)
	const daysMap: { [key: string]: number } = {
		Sun: 0,
		Mon: 1,
		Tue: 2,
		Wed: 3,
		Thu: 4,
		Fri: 5,
		Sat: 6
	};

	// If no days are specified, return a cron expression for daily execution
	if (!daysSent || daysSent.length === 0) {
		return `${minutes} ${hours} * * *`;
	}

	// Map the `daysSent` array to cron day values
	const daysCron = daysSent.map((day) => daysMap[day]).join(',');

	// Return the cron expression
	return `${minutes} ${hours} * * ${daysCron}`;
}

export function deepseekJsonParser(input: string): any[] {
	let resultsStr = '';
	// Check if it contains <think> | which is r1
	if (input.includes('<think>')) {
		// Remove all text from <think> to </think>
		const thinkStr = input.split('</think>').pop() || '';
		resultsStr = thinkStr.split('```json').pop()?.split('```')[0] || '';
		const cleanStr = resultsStr.replace(/\n/g, ' ');
		return JSON.parse(cleanStr);
	}
	if (input.includes('```json')) {
		resultsStr = input.split('```json').pop()?.split('```')[0] || '';
		const cleanStr = resultsStr.replace(/\n/g, ' ');
		return JSON.parse(cleanStr);
	}
	if (input.includes('```')) {
		const match = input.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
		if (match && match[1]) {
			const resultsStr = match[1];
			const cleanStr = resultsStr.replace(/\n/g, ' ');
			return JSON.parse(cleanStr);
		} else {
			console.warn('Failed to extract JSON block using regex.');
		}
	}
	resultsStr = input
		.replace(/```json|```/g, '') // Remove markdown markers
		.replace(/’/g, "'") // Normalize apostrophes
		.replace(/“/g, '"'); // Normalize quotes

	// First pass: Remove non-escaped newlines outside quotes
	let cleanStr = resultsStr.replace(/\n(?!\\)/g, '');

	// Second pass: Fix newlines within quoted strings
	cleanStr = cleanStr.replace(/("(\\"|[^"])*")/g, (match) => match.replace(/\n/g, '\\n'));

	try {
		return JSON.parse(cleanStr);
	} catch (error) {
		console.warn(`JSON parsing failed: ${(error as Error).message}`);
		// Use backup of replacing all newlines with spaces
		cleanStr = resultsStr.replace(/\n/g, ' ');
		return JSON.parse(cleanStr);
	}
}

export function csvToJson(csvText: string): object[] {
	const lines = csvText.trim().split('\n'); // Split the text into lines
	const headers = lines[0].split(',').map((header) => header.trim()); // Get headers and trim spaces

	const jsonArray = lines.slice(1).map((line) => {
		const values = line.split(',').map((value) => value.trim()); // Split each line by commas and trim spaces
		const jsonObject: any = {};

		headers.forEach((header, index) => {
			jsonObject[header] = values[index] || null; // Map headers to their corresponding values
		});

		return jsonObject;
	});

	return jsonArray;
}

export const stringSimilarity = (str1: string, str2: string): number => {
	const len1 = str1.length;
	const len2 = str2.length;
	const matrix: number[][] = [];

	for (let i = 0; i <= len1; i++) {
		matrix[i] = [i];
	}
	for (let j = 0; j <= len2; j++) {
		matrix[0][j] = j;
	}
	for (let i = 1; i <= len1; i++) {
		for (let j = 1; j <= len2; j++) {
			if (str1.charAt(i - 1) === str2.charAt(j - 1)) {
				matrix[i][j] = matrix[i - 1][j - 1];
			} else {
				matrix[i][j] = Math.min(
					matrix[i - 1][j - 1] + 1, // substitution
					matrix[i][j - 1] + 1, // insertion
					matrix[i - 1][j] + 1 // deletion
				);
			}
		}
	}

	return 1 - matrix[len1][len2] / Math.max(len1, len2);
};

// Function to find the closest match for a target field
export const findClosestMatch = (target: string, headers: string[]): string => {
	let bestMatch = headers[0];
	let highestSimilarity = 0;

	headers.forEach((header) => {
		const similarity = stringSimilarity(target.toLowerCase(), header.toLowerCase());
		if (similarity > highestSimilarity) {
			highestSimilarity = similarity;
			bestMatch = header;
		}
	});

	return bestMatch;
};

export const parseEmailsFromCSV = (csvText: string): EmailRecord[] => {
	const jsonArray = csvToJson(csvText);

	// Extract headers from the first object in the JSON array
	const headers = Object.keys(jsonArray[0]);

	// Find the closest matches for "email" and "is_bounce"
	const emailField = findClosestMatch('email', headers);
	const isBounceField = findClosestMatch('is_bounce', headers);

	// Map the JSON array to EmailRecord array
	return jsonArray
		.map((record) => {
			const email = record[emailField]?.trim();
			const isBounce = record[isBounceField]?.trim().toLowerCase() === 'true';

			// Skip records where email is unclear or missing
			if (!email) {
				return null;
			}

			return {
				email,
				is_bounce: isBounce
			};
		})
		.filter((record) => record !== null) as EmailRecord[]; // Filter out null records
};

export const parseEmailsFromJson = (jsonArray: object[]): EmailRecord[] => {
	// Extract headers from the first object in the JSON array
	const headers = Object.keys(jsonArray[0]);

	// Find the closest matches for "email" and "is_bounce"
	const emailField = findClosestMatch('email', headers);
	const isBounceField = findClosestMatch('is_bounce', headers);

	// Map the JSON array to EmailRecord array
	return jsonArray
		.map((record) => {
			const email = record[emailField]?.trim();
			const isBounce = record[isBounceField]?.trim().toLowerCase() === 'true';

			// Skip records where email is unclear or missing
			if (!email) {
				return null;
			}

			return {
				email,
				is_bounce: isBounce
			};
		})
		.filter((record) => record !== null) as EmailRecord[]; // Filter out null records
};

export function extractNameFromEmail(email: string): { firstName?: string; lastName?: string } {
	const localPart = email.substring(0, email.indexOf('@'));

	// Helper function to capitalize the first character
	function capitalize(name: string): string {
		return name.charAt(0).toUpperCase() + name.slice(1);
	}

	// Patterns to match different email formats
	const patterns = [
		// '{firstName}.{lastName}'
		/^([a-zA-Z]+)\.([a-zA-Z]+)$/,
		// '{firstName}{lastName}'
		/^([a-zA-Z]+)([A-Z][a-zA-Z]+)$/, // Assumes last name starts with a capital letter
		// '{firstName}'
		/^([a-zA-Z]+)$/,
		// '{fChar}{lastName}'
		/^([a-zA-Z])([a-zA-Z]+)$/
	];

	for (const pattern of patterns) {
		const match = localPart.match(pattern);
		if (match) {
			const [, firstName, lastName] = match;
			return {
				firstName: capitalize(firstName),
				lastName: lastName ? capitalize(lastName) : undefined
			};
		}
	}

	// Attempt to split by common separators if patterns fail
	const separators = ['.', '_', '-', '+'];
	for (const sep of separators) {
		if (localPart.includes(sep)) {
			const parts = localPart.split(sep).filter(Boolean);
			if (parts.length === 2) {
				return { firstName: capitalize(parts[0]), lastName: capitalize(parts[1]) };
			} else if (parts.length === 3) {
				// '{firstName}.{middleName}.{lastName}'
				return { firstName: capitalize(parts[0]), lastName: capitalize(parts[2]) };
			}
		}
	}

	// Fallback to return the entire local part as the first name
	return { firstName: capitalize(localPart) };
}

export const getBaseUrl = () => {
	if (process.env.NODE_ENV === 'development') {
		return 'https://nos.ngrok.io';
	} else if (process.env.VERCEL_ENV === 'production') {
		// Use the url that it's on
		if (process.env.VERCEL_URL) {
			if (process.env.VERCEL_URL.includes('nosagent.com')) {
				return `https://nosagent.com`;
			} else if (process.env.VERCEL_URL.includes('nos.email')) {
				return `https://nos.email`;
			}
		}
		return 'https://nosagent.com';
	} else {
		return `https://${process.env.VERCEL_BRANCH_URL}`;
	}
};

export const getCookieDomain = () => {
	const baseUrl = getBaseUrl();
	const { hostname } = new URL(baseUrl);
	if (process.env.CUSTOM_ENV === '1') {
		// Return the root domain, for example: if it's stream.nosagent.com - return .nosagent.com
		return `.${hostname.split('.').slice(-2).join('.')}`;
	}
	return `.${hostname}`;
};

/**
 * Safely parse a URL string and return a URL object
 * @param url - The URL string to parse
 * @returns URL object or null if parsing fails
 */
export function safeParseURL(url?: string | null): URL | null {
	if (!url) return null;
	try {
		return new URL(url);
	} catch (error) {
		console.error(`Failed to parse URL: ${url}`, error);
		return null;
	}
}

/**
 * Extract clean domain from a URL string (removes www. prefix)
 * @param url - The URL string to parse
 * @param defaultValue - Optional default value to return if parsing fails
 * @returns Clean domain or defaultValue if parsing fails
 */
export function getCleanDomain(url?: string | null, defaultValue: string = ''): string {
	if (!url?.includes('http')) {
		return url ? url.replace(/^www\./, '') : defaultValue;
	}
	const parsedUrl = safeParseURL(url);
	if (!parsedUrl?.hostname) return defaultValue;
	return parsedUrl.hostname.replace(/^www\./, '');
}

/**
 * Get search parameters from a URL with default values
 * @param url - The URL string to parse
 * @param paramName - Parameter name to extract
 * @param defaultValue - Default value if parameter is not found or parsing fails
 * @returns Parameter value or default value
 */
export function getURLParam<T>(url: string, paramName: string, defaultValue: T): T {
	const parsedUrl = safeParseURL(url);
	if (!parsedUrl) return defaultValue;

	const value = parsedUrl.searchParams.get(paramName);
	if (value === null) return defaultValue;

	// Handle number type conversion
	if (typeof defaultValue === 'number') {
		return Number(value) as unknown as T;
	}

	// Handle boolean type conversion
	if (typeof defaultValue === 'boolean') {
		return (value.toLowerCase() === 'true') as unknown as T;
	}

	return value as unknown as T;
}

function wait(delay: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, delay));
}

export function debounce<T extends (...args: any[]) => void>(
	func: T,
	wait: number
): (...args: Parameters<T>) => void {
	let timeoutId: ReturnType<typeof setTimeout> | undefined;

	const debounced = function (this: any, ...args: Parameters<T>): void {
		const context = this;

		const later = function () {
			timeoutId = undefined;
			func.apply(context, args);
		};

		clearTimeout(timeoutId);
		timeoutId = setTimeout(later, wait);
	};

	return debounced;
}

export async function retryWithBackoff<T>(
	fn: () => Promise<T>,
	retries: number,
	delay: number
): Promise<T> {
	let attempt = 0;

	while (attempt < retries) {
		try {
			console.log(`Attempt ${attempt + 1} of ${retries} to execute the function`);
			// Attempt to execute the function
			return await fn();
		} catch (error) {
			console.error(`Attempt ${attempt + 1} failed: ${error.message}, ${error.stack}`);
			attempt++;
			if (attempt >= retries) {
				throw error; // Re-throw the error if max retries are reached
			}
			// Wait for the specified delay before retrying
			await wait(delay);
		}
	}

	// Should never reach here, but TypeScript requires a return value
	throw new Error('Unexpected error in retry logic');
}

export function levenshtein(a: string, b: string): number {
	const matrix = Array.from({ length: a.length + 1 }, (_, i) =>
		Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
	);

	for (let i = 1; i <= a.length; i++) {
		for (let j = 1; j <= b.length; j++) {
			if (a[i - 1] === b[j - 1]) {
				matrix[i][j] = matrix[i - 1][j - 1];
			} else {
				matrix[i][j] = Math.min(
					matrix[i - 1][j] + 1, // Deletion
					matrix[i][j - 1] + 1, // Insertion
					matrix[i - 1][j - 1] + 1 // Substitution
				);
			}
		}
	}

	return matrix[a.length][b.length];
}

export function levenshteinPercentage(a: string, b: string): number {
	const matrix = Array.from({ length: a.length + 1 }, (_, i) =>
		Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
	);

	for (let i = 1; i <= a.length; i++) {
		for (let j = 1; j <= b.length; j++) {
			if (a[i - 1] === b[j - 1]) {
				matrix[i][j] = matrix[i - 1][j - 1];
			} else {
				matrix[i][j] = Math.min(
					matrix[i - 1][j] + 1, // Deletion
					matrix[i][j - 1] + 1, // Insertion
					matrix[i - 1][j - 1] + 1 // Substitution
				);
			}
		}
	}

	const distance = matrix[a.length][b.length];
	const maxLength = Math.max(a.length, b.length);
	return ((maxLength - distance) / maxLength) * 100; // Return similarity percentage
}
